import { Fragment, type ComponentChildren } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import * as stylex from '@stylexjs/stylex';
import { ArrowDownIcon, ArrowUpIcon, CheckIcon, ChevronRightIcon, CloseIcon } from '@xantory/icons';
import { Spinner } from '../Spinner/spinner';
import { Skeleton } from '../Skeleton/skeleton';
import { styles } from './table.styles';
import { useT } from '@xantory/i18n';
import { useFillHeight } from '../_hooks/use-fill-height';
import { LEDGER_GROUP_PX, scrollRows, type LedgerProps } from './ledger.model';
import { LedgerCols, LedgerFooter, LedgerFrame, LedgerHead, LedgerPill, LedgerTail } from './table-ledger';

/** Widths of the skeleton bars in a loading ledger, cycled across rows and columns. */
const SKELETON_WIDTHS = [80, 50, 75, 60, 70, 60, 70];
const SKELETON_ROWS = 8;

/** A column as the Table uses it internally: `width` already resolved to a CSS string. */
export type ResolvedColumn<T> = Omit<Column<T>, 'width'> & {
  width?: string | undefined;
};
export interface Column<T> {
  key: string;
  header: string;
  /** Cell content. Defaults to the raw `value`. */
  render?: (row: T) => ComponentChildren;
  /** Sort/filter key. Defaults to `row[key]`. */
  value?: (row: T) => string | number;
  sortable?: boolean;
  filterable?: boolean;
  /** Two-value/enum filter: click the header to cycle All → A → B → All (no dropdown). */
  cycle?: boolean;
  /** Explicit filter choices; derived from the data when omitted. */
  filterOptions?: {
    value: string;
    label: string;
  }[];
  align?: 'left' | 'center' | 'right';
  mono?: boolean;
  /** A CSS width, or — in a ledger — a function of the table's width in px, for a column
   *  whose size tracks the screen by a rule a single percentage cannot state (e.g. the plain
   *  table's auto layout: a fixed part plus a share of the slack). */
  width?: string | ((tableWidth: number) => string);
  /** One line, cut with an ellipsis instead of widening the column. Pair it with
   *  `width: '100%'` on the ONE column that should take the slack. */
  truncate?: boolean;
  /** A ledger cell is one line by default; this lets the text wrap (long words break), for the
   *  columns whose plain table wrapped them. Pair it with a taller `rowPx`. */
  wrap?: boolean;
}
interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  rowKey: (row: T) => string;
  /** When set, rows become click-to-expand and render this in a detail row. */
  expandedContent?: (row: T) => ComponentChildren;
  /** R-244 — when set, the whole row OPENS the record instead of expanding it.
   *
   *  A record that is a PROCESS — an incident, a change — is a page, not a drawer:
   *  it has phases, its own actions, and a URL somebody can send you. Collapsing one
   *  back into the row it came from loses all three. The prototype makes the same
   *  call (`<tr class="click" data-act="openInc">` swaps the whole view).
   *
   *  ⚠ Mutually exclusive with `expandedContent` — a row cannot both expand and
   *  navigate, and if both are passed the expander wins (it is the older contract).
   *  The trailing Open button is the keyboard/AT path to the same destination, the
   *  way the chevron is for the expander. */
  onRowClick?: ((row: T) => void) | undefined;
  /** Fired whenever the active sort COLUMN changes, `null` meaning the table has
   *  fallen back to the order `data` was given in.
   *
   *  For a caller whose ROWS carry order-dependent presentation — the Cultivation
   *  Profiles table indents each version under its parent — a header sort silently
   *  invalidates that: the rows move, the indents do not, and the table starts
   *  claiming a lineage that is not there. Knowing a sort is active is what lets
   *  such a caller drop back to a flat rendering. Tables without that problem can
   *  ignore it. */
  onSortChange?: ((key: string | null) => void) | undefined;
  /** Row key to start expanded — for deep links that address one record (R-173).
   *  Only the INITIAL state: the operator can collapse it like any other row. */
  initialExpandedKey?: string | undefined;
  empty?: string;
  /** A trailing editor row, keyed BY COLUMN KEY. For in-table editing, where the
   *  draft row belongs inside the table rather than in a form beneath it (R-302,
   *  the prototype's `editrow`).
   *
   *  R-239 changed this from raw `<td>` children to a map, because the caller
   *  supplying its own cells is exactly how the row drifted out of alignment: the
   *  hand-written `<td>`s carried neither the cell padding nor the column's
   *  `align`, so every control sat left of its header and the numeric columns
   *  read right-aligned in the header and left-aligned in the editor. Keying on
   *  the column means `Table` applies both, and a cell CANNOT disagree with the
   *  header above it. Omit a key for an empty cell.
   *
   *  ⚠ Its presence SUPPRESSES the empty state: a table showing an editor is not
   *  empty, and rendering "no rows yet" above the row you are filling in reads
   *  as a bug. */
  footCells?: Record<string, ComponentChildren> | undefined;
  /** Caps the scroll envelope's height (default 600px, `wrap` in
   *  table.styles.ts) — a caller embedding this in something shorter than
   *  that, like a drawer, passes a smaller value so ITS body scrolls the
   *  table internally instead of the whole panel growing around it. */
  maxHeight?: string | undefined;
  /** Controls that share the reserved clear-filters row instead of stacking a
   *  second bar above it: `toolbarStart` (filters) on the left, `toolbarEnd`
   *  (the table's primary action, e.g. "New item") on the right, "Clear
   *  filters" between them. A caller-owned bar above a sortable table used to
   *  sit on top of that reserved row, so a lone button read ~50px away from its
   *  table (Warehouse, 2026-09-28). */
  toolbarStart?: ComponentChildren;
  toolbarEnd?: ComponentChildren;
  /** The rows on screen are being replaced (a filter changed and the new page
   *  is in flight): keep them, dimmed and inert, under a spinner — the toolbar
   *  stays live, so the page never swaps itself out for a loader. */
  busy?: boolean | undefined;
  /** Called when the body is scrolled to (or already shows) its last row — the
   *  hook for revealing the next page of an in-memory list (`useLoadMore`), which
   *  is what replaced the "Load more" button. May fire again after the rows
   *  grow; a caller with nothing left to show simply ignores it. */
  onEndReached?: (() => void) | undefined;
  /** Grow (or shrink) the body so the PAGE exactly fills its scroll area: the
   *  table ends where the screen does, on a laptop, a 27" monitor or a TV alike,
   *  with whatever sits below it (a count, the page's padding) still in view.
   *  A fixed `calc(100dvh - Npx)` could only be right for one layout — it left a
   *  constant 183px band under the table and, on a tall screen, the rows ran out
   *  long before it (user, 2026-09-30). Overrides `maxHeight`; never below
   *  FILL_MIN, so a phone's long stacked header scrolls the page instead. */
  fill?: boolean | undefined;
  /** The scrolling-list pattern from canvas design 1a: a square bordered frame, a
   *  light-green header, 44px rows, and under the rows a footer with the position
   *  ("Rows 191–200 · 200 of 412 loaded"), a progress bar and the list's state —
   *  plus the paused / error / end rows and the "N new entries" pill. Drive it with
   *  `useLedger`. Overrides `fill`/`maxHeight` (the box is 560px tall at most).
   *
   *  ⚠ Plain columns only for now: a sortable/filterable header keeps the old
   *  padding, so its label sits 12px off its column. Sorting a list that only
   *  reveals part of itself is also misleading — do both when a ledger page needs
   *  them, not before. */
  ledger?: LedgerProps | undefined;
  /** Ledger only: a full-width heading row above each run of rows that share a key
   *  ("Today", "Yesterday" …), with how many rows are under it. Rows must already be
   *  in group order — nothing is re-sorted. Not for a sortable table: a sort would
   *  shatter the runs. */
  groupBy?: ((row: T) => {
    key: string;
    label: string;
  }) | undefined;
  /** Ledger only: a 3px primary bar on the row's left edge (an unread record). */
  rowMark?: ((row: T) => boolean) | undefined;
}
type SortDir = 'asc' | 'desc';
function ChevronIcon({
  open
}: {
  open: boolean;
}) {
  return <ChevronRightIcon {...stylex.props(styles.chev, open && styles.chevOpen)} />;
}
export function Table<T>({
  columns: columnsProp,
  data,
  rowKey,
  expandedContent,
  onRowClick,
  onSortChange,
  initialExpandedKey,
  empty,
  footCells,
  maxHeight,
  toolbarStart,
  toolbarEnd,
  busy = false,
  onEndReached,
  fill = false,
  ledger,
  groupBy,
  rowMark
}: TableProps<T>) {
  const t = useT('ui');
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const filterActive = Object.values(filters).some(a => a.length > 0);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  // viewport coords of the open menu's header button — the menu is position:fixed so it escapes the
  // table's overflow clip (no clipping on short lists, no min-height void needed).
  const [menuRect, setMenuRect] = useState<{
    top: number;
    left: number;
    right: number;
  } | null>(null);
  const [menuBtn, setMenuBtn] = useState<HTMLButtonElement | null>(null);
  const [expanded, setExpanded] = useState<string | null>(initialExpandedKey ?? null);

  // ── the scroll envelope: "more below" + a scrollbar while you scroll ──────
  const wrapRef = useRef<HTMLDivElement>(null);
  /** Rows exist below the visible part of the body — drives the floating ↓. */
  const [moreBelow, setMoreBelow] = useState(false);
  /** True for a second after each scroll; `.scroll-reveal` draws the thumb. */
  const [scrolling, setScrolling] = useState(false);
  const idle = useRef(0);
  const endRef = useRef(onEndReached);
  endRef.current = onEndReached;
  const ledgerRef = useRef(ledger);
  ledgerRef.current = ledger;
  /** Where each group heading sits (row indices), for the footer's row arithmetic. */
  const groupStartsRef = useRef<number[]>([]);
  /** The ledger footer's readout: only re-rendered when a row boundary is crossed. */
  const [view, setView] = useState({
    topRow: 0,
    bottomRow: 0,
    far: false
  });
  /** The body's scrollbar width, so the ledger's separate header table matches it. */
  const [gutter, setGutter] = useState(0);
  /** The body's width in px, so a `width` function has something to read. */
  const [tableW, setTableW] = useState(0);
  /** A ledger whose columns overshoot the box by a pixel or two (rounding in the widths) would show
   *  a sideways scrollbar for nothing — so within a few px it clips instead of scrolling. */
  const [snug, setSnug] = useState(false);

  // `width` functions resolved against the measured table width (0 until the first measure,
  // which the function must tolerate).
  const columns = columnsProp.map(c => typeof c.width === 'function' ? {
    ...c,
    width: c.width(tableW)
  } : c) as ResolvedColumn<T>[];
  const headRef = useRef<HTMLDivElement>(null);

  /** One measurement for both signals, run on scroll and whenever the rows or
   *  the box change size. 48px of slack = "at the end" before the last pixel,
   *  so the next page is already there when the eye gets to it. */
  const measure = () => {
    const el = wrapRef.current;
    if (!el) return;
    const left = el.scrollHeight - el.scrollTop - el.clientHeight;
    setMoreBelow(left > 8);
    const lg = ledgerRef.current;
    if (lg) {
      setGutter(el.offsetWidth - el.clientWidth);
      setTableW(el.clientWidth);
      const over = el.scrollWidth - el.clientWidth;
      setSnug(over > 0 && over <= 4);
      if (headRef.current) headRef.current.scrollLeft = el.scrollLeft;
      const {
        topRow,
        bottomRow
      } = scrollRows(el.scrollTop, el.clientHeight, lg.rowPx, groupStartsRef.current, LEDGER_GROUP_PX);
      const far = el.scrollTop > 400;
      setView(v => v.topRow === topRow && v.bottomRow === bottomRow && v.far === far ? v : {
        topRow,
        bottomRow,
        far
      });
      lg.onAtTop(el.scrollTop <= 8);
      // 240px of slack for a ledger: the next page is revealed well before the end.
      if (left <= 240) lg.onEndReached();
    } else if (left <= 48) endRef.current?.();
  };
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    measure();
    // A resize (viewport, a sibling notice appearing) changes the box, not the rows.
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [data.length, maxHeight]);
  useEffect(() => () => window.clearTimeout(idle.current), []);

  // ── fill: the body takes exactly the room the page has left ─────────────
  // (shared with any other scrolling list via `useFillHeight` — not Table-only)
  // A ledger CAPS its height at the room left instead of forcing it: a short list hugs its
  // rows (three plans do not stand in a screen-tall frame), a long one fills the screen.
  useFillHeight(wrapRef, fill, undefined, ledger ? 'maxHeight' : 'height');
  const toTop = () => wrapRef.current?.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
  const onScroll = () => {
    measure();
    setScrolling(true);
    window.clearTimeout(idle.current);
    idle.current = window.setTimeout(() => setScrolling(false), 1000);
  };

  /** Most of a screenful, so the row that was last in view stays in view. */
  const scrollDown = () => {
    const el = wrapRef.current;
    if (el) el.scrollBy({
      top: Math.round(el.clientHeight * 0.8),
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    if (openMenu == null) return;
    const close = () => setOpenMenu(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpenMenu(null);
        menuBtn?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    // close on scroll too (capture, to catch the table's own scroll container) so the fixed menu
    // can't drift away from its button.
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [openMenu, menuBtn]);
  const cellValue = (col: ResolvedColumn<T>, row: T): string | number => col.value ? col.value(row) : (row as Record<string, unknown>)[col.key] as string | number;
  const optionsFor = (col: ResolvedColumn<T>) => col.filterOptions ?? Array.from(new Set(data.map(r => String(cellValue(col, r))))).map(v => ({
    value: v,
    label: v
  }));
  const rows = useMemo(() => {
    let out = data;
    for (const col of columns) {
      const sel = filters[col.key];
      if (sel && sel.length) out = out.filter(r => sel.includes(String(cellValue(col, r))));
    }
    if (sortKey) {
      const col = columns.find(c => c.key === sortKey);
      if (col) {
        out = [...out].sort((a, b) => {
          const av = cellValue(col, a);
          const bv = cellValue(col, b);
          const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
          return sortDir === 'asc' ? cmp : -cmp;
        });
      }
    }
    return out;
  }, [data, columns, filters, sortKey, sortDir]);

  // The expander owns the extra column when both are somehow passed; `navigable`
  // is what every other branch below keys on so the two can never half-apply.
  const navigable = !expandedContent && onRowClick != null;
  const totalCols = columns.length + (expandedContent ? 1 : 0);

  // Group headings (ledger): where each run starts and how long it is.
  const groups = useMemo(() => {
    const starts: number[] = [];
    const counts = new Map<string, number>();
    if (ledger && groupBy) {
      let prev: string | null = null;
      rows.forEach((r, i) => {
        const k = groupBy(r).key;
        if (k !== prev) starts.push(i);
        counts.set(k, (counts.get(k) ?? 0) + 1);
        prev = k;
      });
    }
    return {
      starts,
      counts
    };
  }, [rows, groupBy, ledger]);
  groupStartsRef.current = groups.starts;
  const setSort = (key: string, dir: SortDir) => {
    if (sortKey === key && sortDir === dir) setSortKey(null);else {
      setSortKey(key);
      setSortDir(dir);
    }
    setOpenMenu(null);
  };
  const toggleFilter = (key: string, value: string) => {
    setFilters(f => {
      const cur = f[key] ?? [];
      const next = cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value];
      return {
        ...f,
        [key]: next
      };
    });
  };

  // Cycle filter: All → opt0 → opt1 → … → All (single-select), like the prototype's cycleStatus.
  const cycleFilter = (col: ResolvedColumn<T>) => {
    const opts = optionsFor(col);
    const cur = filters[col.key]?.[0];
    const idx = cur == null ? -1 : opts.findIndex(o => o.value === cur);
    const nextOpt = opts[idx + 1];
    setFilters(f => ({
      ...f,
      [col.key]: nextOpt ? [nextOpt.value] : []
    }));
    setOpenMenu(null);
  };

  // Cycle sort: none → asc → desc → none. Sorting is a two-state choice, so it clicks-to-cycle like the
  // 2-option filters instead of opening a dropdown — consistent header interaction (the arrow shows state).
  const cycleSort = (key: string) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('asc');
    } else if (sortDir === 'asc') setSortDir('desc');else {
      setSortKey(null);
      setSortDir('asc');
    }
    setOpenMenu(null);
  };

  // ONE notification point for every path that can change the sort — the header
  // cycle, the column menu and Clear filters all land here, so none can be
  // forgotten. Held in a ref so a caller passing an inline arrow (the normal
  // case) cannot turn this into a render loop.
  const sortCb = useRef(onSortChange);
  sortCb.current = onSortChange;
  useEffect(() => {
    sortCb.current?.(sortKey);
  }, [sortKey]);
  // A ledger only holds part of its list on screen: a sort or a filter over THAT part
  // would put a row at the top that does not belong there, or hide one that matches. So
  // either one reveals every row; clearing both starts the paging over.
  useEffect(() => {
    ledgerRef.current?.onSort(sortKey != null || filterActive);
  }, [sortKey, filterActive]);
  const ariaSort = (key: string): 'ascending' | 'descending' | undefined => sortKey === key ? sortDir === 'asc' ? 'ascending' : 'descending' : undefined;
  const hasActive = sortKey != null || filterActive;
  const clearAll = () => {
    setFilters({});
    setSortKey(null);
    setOpenMenu(null);
  };

  // R-304: reserve the clear-filters row ONLY on a table where a filter or sort
  // can actually appear. Keeping the height when something IS toggleable is the
  // point — it stops the table jumping down the moment you filter. Reserving it
  // on a table with no interactive column just spends 39px (27 + its 12px margin)
  // above every read-only table in the console, and inside a `Card` that reads as
  // a gap under the card's own title.
  const canInteract = columns.some(c => c.sortable || c.filterable);
  // A ledger that only sorts has nothing for "Clear filters" to clear that the header does not
  // already (clicking the sorted header cycles it off), so it keeps no row for it — the empty row
  // it reserved read as a hole between the page's own filters and the table.
  const showClear = canInteract && !(ledger && !columns.some(c => c.filterable));
  // §24 — the scroll envelope's scrollbar is invisible AT REST and drawn while
  // the table is hovered or scrolling (`scroll-reveal`, reset.css); `tabIndex`
  // keeps a long table keyboard-reachable either way.
  const wrapProps = stylex.props(styles.wrap, ledger && styles.wrapLedger, busy && styles.wrapBusy);
  // The ledger swaps the header/cell faces wholesale (see `thLedger`).
  // With a filter on, every row is revealed (see the effect above), so the footer counts
  // what the filter LEFT — "Rows 1–12 · 7 of 7 loaded", not the unfiltered total.
  const ledgerView = ledger && filterActive ? {
    ...ledger,
    loaded: rows.length,
    total: rows.length,
    paused: false,
    ended: rows.length > 0
  } : ledger;
  const thS = ledger ? styles.thLedger : styles.th;
  const tdS = ledger ? styles.tdLedger : styles.td;

  /** One header cell — for the plain header AND the ledger's separate one (which hands
   *  this to `LedgerHead`), so a sortable / filterable column behaves identically in
   *  both. In a ledger the `th` itself carries the 8/20px padding, so its button adds
   *  none (`thBtnLedger`) and `thCol`'s reset is not applied. */
  const renderHeadCell = (col: ResolvedColumn<T>, i: number) => {
    const btnS = ledger ? styles.thBtnLedger : styles.thBtn;
    const interactive = col.sortable || col.filterable;
    const active = sortKey === col.key || (filters[col.key]?.length ?? 0) > 0;
    if (!interactive) {
      return <th key={col.key} style={col.width ? {
        width: col.width
      } : undefined} {...stylex.props(thS, col.align === 'right' && styles.alignRight, col.align === 'center' && styles.alignCenter)}>
          {col.header}
        </th>;
    }
    const lastTwo = i >= columns.length - 2;
    // A single-select filter with exactly two options reads better as a click-to-cycle header
    // (All → A → B → All) than a dropdown. Auto-applies; `col.cycle` forces it explicitly.
    const isCycle = col.cycle || !!col.filterable && !col.sortable && optionsFor(col).length === 2;
    if (isCycle) {
      const curVal = filters[col.key]?.[0];
      const curLabel = curVal != null ? optionsFor(col).find(o => o.value === curVal)?.label : undefined;
      return <th key={col.key} style={col.width ? {
        width: col.width
      } : undefined} {...stylex.props(thS, !ledger && styles.thCol)}>
          <button type="button" onClick={e => {
          e.stopPropagation();
          cycleFilter(col);
        }} {...stylex.props(btnS, active && styles.thBtnActive, col.align === 'right' && styles.thBtnRight, col.align === 'center' && styles.thBtnCenter)}>
            {col.header}
            {curLabel && <span {...stylex.props(styles.cyc)}>· {curLabel}</span>}
            <ArrowUpIcon strokeWidth={2.2} {...stylex.props(styles.thIco, active && styles.thIcoActive)} />
          </button>
        </th>;
    }
    // Sort-only column: click the header to cycle none → asc → desc (no dropdown), same as the
    // 2-option filter cycle above. Columns that also filter keep the dropdown (handled below).
    if (col.sortable && !col.filterable) {
      const dir = sortKey === col.key ? sortDir : null;
      return <th key={col.key} aria-sort={ariaSort(col.key)} style={col.width ? {
        width: col.width
      } : undefined} {...stylex.props(thS, !ledger && styles.thCol)}>
          <button type="button" onClick={e => {
          e.stopPropagation();
          cycleSort(col.key);
        }} {...stylex.props(btnS, dir && styles.thBtnActive, col.align === 'right' && styles.thBtnRight, col.align === 'center' && styles.thBtnCenter)}>
            {col.header}
            <ArrowUpIcon strokeWidth={2.2} {...stylex.props(styles.thIco, dir && styles.thIcoActive, dir === 'desc' && styles.thIcoDesc)} />
          </button>
        </th>;
    }
    return <th key={col.key} aria-sort={col.sortable ? ariaSort(col.key) : undefined} style={col.width ? {
      width: col.width
    } : undefined} {...stylex.props(thS, !ledger && styles.thCol)}>
        <button type="button" aria-expanded={openMenu === col.key} aria-haspopup="menu" onClick={e => {
        e.stopPropagation();
        if (openMenu === col.key) {
          setOpenMenu(null);
          return;
        }
        const r = e.currentTarget.getBoundingClientRect();
        setMenuRect({
          top: r.bottom + 4,
          left: r.left,
          right: window.innerWidth - r.right
        });
        setMenuBtn(e.currentTarget);
        setOpenMenu(col.key);
      }} {...stylex.props(btnS, active && styles.thBtnActive, col.align === 'right' && styles.thBtnRight, col.align === 'center' && styles.thBtnCenter)}>
          {col.header}
          <ArrowUpIcon strokeWidth={2.2} {...stylex.props(styles.thIco, active && styles.thIcoActive, sortKey === col.key && sortDir === 'desc' && styles.thIcoDesc)} />
        </button>
        {openMenu === col.key && menuRect && <div onClick={e => e.stopPropagation()} {...stylex.props(styles.menu)} style={lastTwo ? {
        top: `${menuRect.top}px`,
        right: `${menuRect.right}px`
      } : {
        top: `${menuRect.top}px`,
        left: `${menuRect.left}px`
      }}>
            {col.sortable && <>
                <div {...stylex.props(styles.menuLabel)}>{t('sort')}</div>
                <button type="button" onClick={() => setSort(col.key, 'asc')} {...stylex.props(styles.menuItem, sortKey === col.key && sortDir === 'asc' && styles.menuItemOn)}>
                  {t('ascending')}
                </button>
                <button type="button" onClick={() => setSort(col.key, 'desc')} {...stylex.props(styles.menuItem, sortKey === col.key && sortDir === 'desc' && styles.menuItemOn)}>
                  {t('descending')}
                </button>
              </>}
            {col.sortable && col.filterable && <div {...stylex.props(styles.sep)} />}
            {col.filterable && <>
                <div {...stylex.props(styles.menuLabel)}>{t('filter')}</div>
                {optionsFor(col).map(opt => {
            const on = (filters[col.key] ?? []).includes(opt.value);
            return <button key={opt.value} type="button" onClick={() => toggleFilter(col.key, opt.value)} {...stylex.props(styles.menuItem, on && styles.menuItemOn)}>
                      {opt.label}
                      <span {...stylex.props(styles.check, on && styles.checkOn)}>
                        <CheckIcon size={11} strokeWidth={3} />
                      </span>
                    </button>;
          })}
              </>}
          </div>}
      </th>;
  };
  return <>
      {(showClear || toolbarStart != null || toolbarEnd != null) && <div {...stylex.props(styles.toolbar)}>
          {toolbarStart != null && <div {...stylex.props(styles.toolbarStart)}>{toolbarStart}</div>}
          {showClear && <button type="button" onClick={clearAll} aria-hidden={!hasActive} {...stylex.props(styles.clearf, !hasActive && styles.clearfHidden)}>
              <CloseIcon size={14} />
              Clear filters
            </button>}
          {toolbarEnd}
        </div>}
      <LedgerFrame on={!!ledger} footer={ledgerView && <LedgerFooter ledger={ledgerView} topRow={view.topRow} bottomRow={view.bottomRow} far={view.far} onTop={toTop} />}>
        {ledger && <LedgerHead columns={columns} gutter={gutter} headRef={headRef} renderCell={renderHeadCell} minWidthPx={ledger.minWidthPx} />}
        <div {...stylex.props(styles.body)} aria-busy={busy || ledger?.state === 'loading' || undefined}>
          <div ref={wrapRef} tabIndex={0} style={{
          ...(fill ? {
            maxHeight: 'none'
          } : ledger ? {} : maxHeight ? {
            maxHeight
          } : {}),
          ...(snug ? {
            overflowX: 'hidden'
          } : {})
        }} onScroll={onScroll} data-scrolling={scrolling || undefined} {...wrapProps} className={[wrapProps.className, 'scroll-reveal'].filter(Boolean).join(' ')}>
            <table {...stylex.props(styles.table, ledger && styles.tableLedger)} style={ledger?.minWidthPx ? {
            minWidth: `${ledger.minWidthPx}px`
          } : undefined}>
              {ledger && <LedgerCols columns={columns} />}
              {!ledger && <thead>
                  <tr>
                    {expandedContent && <th {...stylex.props(thS, styles.thChev)}>
                        {/* axe empty-table-header wants real (if hidden) text, not aria-label */}
                        <span {...stylex.props(styles.srOnly)}>{t('toggleDetails')}</span>
                      </th>}
                    {columns.map((col, i) => renderHeadCell(col, i))}
                  </tr>
                </thead>}
              <tbody>
                {ledger?.state === 'loading' && rows.length === 0 ? Array.from({
                length: SKELETON_ROWS
              }, (_, i) => <tr key={i} aria-hidden="true">
                      {columns.map((col, j) => <td key={col.key} style={ledger.rowPx ? {
                  height: `${ledger.rowPx}px`
                } : undefined} {...stylex.props(styles.tdLedger)}>
                          <Skeleton width={`${SKELETON_WIDTHS[(i + j) % SKELETON_WIDTHS.length]}%`} height="8px" />
                        </td>)}
                    </tr>) : rows.length === 0 && !footCells ?
              // A failed read is not "no rows": the ledger's error row says so instead.
              ledger?.state === 'error' ? null : <tr>
                      <td colSpan={totalCols} {...stylex.props(styles.empty)}>
                        {empty ?? t('noData')}
                      </td>
                    </tr> : rows.map((row, rowIndex) => {
                const key = rowKey(row);
                const group = ledger && groupBy ? groupBy(row) : null;
                const startsGroup = group != null && groups.starts.includes(rowIndex);
                const isOpen = expanded === key;
                return <Fragment key={key}>
                        {group && startsGroup && <tr>
                            <td colSpan={totalCols} {...stylex.props(styles.groupRow)}>
                              {group.label}
                              <span {...stylex.props(styles.groupCount)}>{groups.counts.get(group.key)}</span>
                            </td>
                          </tr>}
                        <tr onClick={expandedContent ? () => setExpanded(isOpen ? null : key) : navigable ? () => onRowClick!(row) : undefined}
                  // A navigable row is its own keyboard target (Tab to it, Enter
                  // or Space opens it). It used to carry a trailing chevron button
                  // for that; the chevron rendered at 0×0 for as long as it existed
                  // and the user asked for it gone (2026-09-28). Keys pressed on a
                  // control INSIDE the row (a link, a button) stay that control's.
                  {...navigable && {
                    tabIndex: 0,
                    'aria-label': t('openRecord'),
                    onKeyDown: (e: KeyboardEvent) => {
                      if (e.target !== e.currentTarget || e.key !== 'Enter' && e.key !== ' ') return;
                      e.preventDefault();
                      onRowClick!(row);
                    }
                  }} {...stylex.props(styles.row, (expandedContent || navigable) && styles.rowClickable, navigable && styles.rowFocusable, isOpen && styles.rowOpen)}>
                          {expandedContent && <td {...stylex.props(styles.td, styles.chevCell)}>
                              {/* Whole-row click stays (locked pattern); this button is the
                               keyboard/AT path to the same toggle. */}
                              <button type="button" aria-expanded={isOpen} aria-label={t('toggleDetails')} onClick={e => {
                        e.stopPropagation();
                        setExpanded(isOpen ? null : key);
                      }} {...stylex.props(styles.chevBtn)}>
                                <ChevronIcon open={isOpen} />
                              </button>
                            </td>}
                          {columns.map((col, colIndex) => <td key={col.key} style={ledger?.rowPx ? {
                      height: `${ledger.rowPx}px`
                    } : undefined} {...stylex.props(tdS, colIndex === 0 && ledger && rowMark?.(row) && styles.tdMarked, col.mono && styles.tdMono, col.truncate && styles.tdTruncate, col.wrap && styles.tdWrap, col.align === 'right' && styles.alignRight, col.align === 'center' && styles.alignCenter)}>
                              {col.render ? col.render(row) : String(cellValue(col, row))}
                            </td>)}
                        </tr>
                        {expandedContent && isOpen && <tr key={`${key}-detail`}>
                            <td colSpan={totalCols} {...stylex.props(styles.detailCell)}>
                              {expandedContent(row)}
                            </td>
                          </tr>}
                      </Fragment>;
              })}
                {footCells && <tr {...stylex.props(styles.footRow)}>
                    {/* the chevron column, when the table has one — otherwise every
                     editor cell would sit one column left of its header */}
                    {expandedContent && <td {...stylex.props(styles.td, styles.footTd, styles.chevCell)} />}
                    {columns.map(col => <td key={col.key} {...stylex.props(tdS, ledger ? styles.footTdLedger : styles.footTd, col.mono && styles.tdMono, col.align === 'right' && styles.alignRight, col.align === 'center' && styles.alignCenter)}>
                        {footCells[col.key] ?? null}
                      </td>)}
                  </tr>}
              </tbody>
            </table>
            {ledger && <LedgerTail ledger={ledgerView ?? ledger} />}
          </div>
          {busy && <div {...stylex.props(styles.busy)}>
              <Spinner size="md" />
            </div>}
          {/* Floats over the last visible rows while more sit below them; hidden
            at the end, so it never points at nothing. A real button — keyboard
            users get the same shortcut. */}
          {ledger && <LedgerPill ledger={ledger} onShow={() => {
          ledger.onShowPending();
          toTop();
        }} />}
          {moreBelow && !busy && !ledger && <button type="button" aria-label={t('scrollDown')} title={t('scrollDown')} onClick={scrollDown} {...stylex.props(styles.moreBelow)}>
              <ArrowDownIcon size={18} />
            </button>}
        </div>
      </LedgerFrame>
    </>;
}